import {
  useCallback,
  useContext,
  createContext,
  useEffect,
  useState,
  ReactNode,
  useRef
} from 'react';
import { AuthService } from '../services/authService';
import { ExtendedUser, Organization } from '../types';
import { Session } from '@supabase/supabase-js';
import { OrganizationService } from '../services/organizationService';
import { UserService } from '../services/userService';
import { UserOrganizationService } from '../services/userOrganizationService';
import NotificationModal from '../components/NotificationModal';

export interface AuthContextType {
  user: ExtendedUser | null;
  userLoaded: boolean;
  loading: boolean;
  session: Session | null;
  organizations: Organization[];
  organizationsLoaded: boolean;
  currentOrganization: Organization | null;
  login: (email: string, password: string) => Promise<{ data: any; error: any }>;
  logout: () => Promise<{ error: any }>;
  createPassword: (name: string, newPassword: string) => Promise<boolean | undefined>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
  switchOrganization: (organization: Organization) => void;
  hasOrganization: () => boolean;
  isEmailVerified: () => boolean;
  needsOrganizationSetup: () => boolean;
  error: string | undefined;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userLoaded: false,
  loading: true,
  session: null,
  organizations: [],
  organizationsLoaded: false,
  currentOrganization: null,
  login: async () => ({ data: null, error: null }),
  logout: async () => ({ error: null }),
  createPassword: async () => undefined,
  resetPassword: async () => ({ data: null, error: null }),
  switchOrganization: () => {},
  hasOrganization: () => false,
  isEmailVerified: () => false,
  needsOrganizationSetup: () => false,
  error: undefined
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [userLoaded, setUserLoaded] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [dismissError, setDismissError] = useState<boolean>(false);
  const [session, setSession] = useState<Session | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationsLoaded, setOrganizationsLoaded] = useState<boolean>(false);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const currentOrganizationRef = useRef<Organization | null>(null);

  const createPassword = async (
    name: string,
    newPassword: string
  ): Promise<boolean | undefined> => {
    try {
      // 1. Update password using AuthService
      const authResult = await AuthService.createPassword(name, newPassword);
      if (!authResult.success) {
        setError(authResult.error);
        return;
      }

      // 2. Update name in public.users table using UserService
      if (user?.id) {
        const result = await UserService.updateUserName(user.id, name);
        if (!result.success) {
          setError(result.message);
          return;
        }
      }

      return true;
    } catch (error) {
      setError((error as Error).message);
      return;
    }
  };

  const login = async (email: string, password: string) => {
    setError(undefined);
    const { data, error } = await AuthService.signInWithPassword(email, password);
    if (error) {
      setError(error.message);
    }
    return { data, error };
  };

  const resetPassword = async (email: string) => {
    setError(undefined);
    const { data, error } = await AuthService.resetPasswordForEmail(
      email,
      `${window.location.origin}/password`
    );
    if (error) {
      setError(error.message);
    }
    return { data, error };
  };

  const fetchUserOrganizations = useCallback(async () => {
    if (!user) return;

    try {
      const orgs = await UserOrganizationService.getUserOrganizations(user.id);
      setOrganizations(orgs);
      setOrganizationsLoaded(true);

      // Update currentOrganization with correct role if it exists in user's organizations
      if (currentOrganizationRef.current) {
        const updatedCurrentOrg = orgs.find(org => org.id === currentOrganizationRef.current!.id);
        if (updatedCurrentOrg) {
          setCurrentOrganization(updatedCurrentOrg);
          OrganizationService.saveCurrentOrganizationToStorage(updatedCurrentOrg);
        } else if (orgs.length > 0) {
          setCurrentOrganization(orgs[0]);
          OrganizationService.saveCurrentOrganizationToStorage(orgs[0]);
        }
      } else if (orgs.length > 0) {
        setCurrentOrganization(orgs[0]);
        OrganizationService.saveCurrentOrganizationToStorage(orgs[0]);
      }
    } catch (error) {
      setError((error as Error).message);
    }
  }, [user]);

  const recoverOrganizationFromStorage = () => {
    const savedOrg = OrganizationService.getCurrentOrganizationFromStorage();
    if (savedOrg) setCurrentOrganization(savedOrg);
  };

  const switchOrganization = (organization: Organization) => {
    setCurrentOrganization(organization);
    OrganizationService.saveCurrentOrganizationToStorage(organization);
  };

  const logout = async () => {
    const { error } = await AuthService.signOut();
    if (error) {
      setError(error.message);
    }
    setOrganizations([]);
    return { error };
  };

  useEffect(() => {
    const fetchSession = async () => {
      const { data } = await AuthService.getSession();
      const { data: userData } = await AuthService.getUser();

      if (data.session) {
        setUser(userData.user as ExtendedUser);
        setSession(data.session);
        setUserLoaded(true);
      } else {
        setUser(null);
        setSession(null);
        setUserLoaded(true);
      }
    };
    fetchSession();

    const { data: subscription } = AuthService.onAuthStateChange(async (_event, session) => {
      if (session) {
        setSession(session);
        setUser(session.user as ExtendedUser);
      } else {
        setSession(null);
        setUser(null);
      }
    });

    return () => {
      subscription?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    recoverOrganizationFromStorage();
  }, []);

  useEffect(() => {
    currentOrganizationRef.current = currentOrganization;
  }, [currentOrganization]);

  useEffect(() => {
    if (user && userLoaded) {
      fetchUserOrganizations();
    }
  }, [user, userLoaded, fetchUserOrganizations]);

  useEffect(() => {
    if (userLoaded) {
      setLoading(false);
    }
  }, [userLoaded]);

  // Helper functions for auth guards
  const hasOrganization = useCallback((): boolean => {
    return organizations.length > 0;
  }, [organizations]);

  const isEmailVerified = useCallback((): boolean => {
    return user?.email_confirmed_at != null;
  }, [user]);

  const needsOrganizationSetup = useCallback((): boolean => {
    return (
      user != null && userLoaded && organizationsLoaded && !error && organizations.length === 0
    );
  }, [user, userLoaded, organizationsLoaded, error, organizations]);

  const contextValue: AuthContextType = {
    user,
    userLoaded,
    loading,
    session,
    organizations,
    organizationsLoaded,
    currentOrganization,
    login,
    logout,
    createPassword,
    resetPassword,
    switchOrganization,
    hasOrganization,
    isEmailVerified,
    needsOrganizationSetup,
    error
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {error && !dismissError && (
        <NotificationModal
          title="Ops! Ocorreu um erro."
          message="Um erro inesperado ocorreu ao carregar os dados da aplicação."
          type="error"
          onClose={() => setDismissError(true)}
        />
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
};
